import React from 'react';
import { NumberInput, required, useTranslate } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import moment from 'moment';
import { calculateEndDate } from '../../../utils/eventFormUtils';
import { MaskedDateTimeInput } from '../event-utils/MaskedDateTimeInput';

/**
 * Date Range Selector Component
 * Handles date range and duration inputs for one-off events
 */
export const DateRangeSelector = ({ disabled = false, isEdit = false }) => {
  const translate = useTranslate();
  return (
    <React.Fragment>
      <MaskedDateTimeInput
        source="startdate"
        label={translate('form.event_start_date', 'Event Start Date')}
        disabled={disabled}
        validate={[
          ...START_DATE_VALIDATION,
          (value) => {
            if (!value) return undefined;
            if (!isEdit && moment(value).isBefore(moment())) {
              return 'Start date should be after today.';
            }
            return undefined;
          },
        ]}
      />
      <NumberInput
        source="duration"
        label={translate('form.event_duration', 'Event Duration (minutes)')}
        fullWidth
        validate={required()}
        disabled={disabled}
      />

      <EndDateDisplay />
    </React.Fragment>
  );
};

const START_DATE_VALIDATION = [
  required(),
  (value) => {
    if (!value) return undefined;
    if (!moment(value).isValid()) return 'Please enter a valid date.';
    return undefined;
  },
];

const toDateOrNull = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  // moment({}) "bugun" uretir; bos/plain object'i tarih sayma
  if (typeof value === 'object') return null;
  const m = moment(value);
  return m.isValid() ? m.toDate() : null;
};

/**
 * Bitis tarihi: DateTimeInput disabled iken bos kalabiliyor.
 * Bu yuzden MUI TextField ile gosterip RHF'ye setValue yaziyoruz.
 */
const EndDateDisplay = () => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const startdate = useWatch({ name: 'startdate' });
  const duration = useWatch({ name: 'duration' });
  const enddate = useWatch({ name: 'enddate' });

  const resolvedEnd = React.useMemo(() => {
    const start = toDateOrNull(startdate);
    const hasDuration = duration != null && duration !== '';
    if (start && hasDuration) {
      const calc = calculateEndDate(start, duration);
      if (calc) return calc;
    }
    // Duration yoksa varsayilan 180 ile goster (formda bos kalmasin)
    if (start && !hasDuration) {
      const calc = calculateEndDate(start, 180);
      if (calc) return calc;
    }
    return toDateOrNull(enddate);
  }, [startdate, duration, enddate]);

  React.useEffect(() => {
    const start = toDateOrNull(startdate);
    if (start && !(startdate instanceof Date)) {
      setValue('startdate', start, { shouldDirty: false });
    }
  }, [startdate, setValue]);

  React.useEffect(() => {
    if (!resolvedEnd) return;
    const current = toDateOrNull(enddate);
    if (!current || current.getTime() !== resolvedEnd.getTime()) {
      setValue('enddate', resolvedEnd, { shouldValidate: true, shouldDirty: false });
    }
  }, [resolvedEnd, enddate, setValue]);

  const displayValue = resolvedEnd ? moment(resolvedEnd).format('DD/MM/YYYY HH:mm') : '';

  return (
    <TextField
      label={translate('form.event_end_date', 'Event End Date')}
      value={displayValue}
      fullWidth
      margin="dense"
      size="small"
      disabled
      InputLabelProps={{ shrink: true }}
      helperText={
        displayValue
          ? translate('form.end_date_auto_calc', 'Start + duration ile otomatik hesaplanır')
          : translate('form.end_date_hint', 'Start date ve duration girilince burada görünür')
      }
      sx={{ mt: 1, mb: 1 }}
    />
  );
};
